import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  User, Briefcase, MapPin, GraduationCap, Code, Languages, Award,
  CheckCircle, Loader2, Camera, QrCode, Share2, Clock, Calendar,
  Building2, TrendingUp, Shield, Globe, Target, ExternalLink,
  Smartphone, Lock, Eye, ChevronRight, Sparkles, Star
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate initials from name
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Format experience years
const formatExperience = (years) => {
  if (!years) return 'Fresher';
  if (years < 1) return 'Less than 1 year';
  if (years === 1) return '1 year';
  return `${years} years`;
};

export default function CandidatePublicProfile() {
  const { username, slug } = useParams();
  const profileIdentifier = slug || username; // Support both /c/:slug and /:username routes
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [error, setError] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  
  // Auth state for claiming profile
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimPhone, setClaimPhone] = useState('');
  const [claimOtp, setClaimOtp] = useState('');
  const [claimStep, setClaimStep] = useState('phone'); // phone, otp
  const [claimLoading, setClaimLoading] = useState(false);
  
  // Logged in state
  const [isOwner, setIsOwner] = useState(false);
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  // Helper to display skill/item
  const displayItem = (item) => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item !== null) {
      // For education objects
      if (item.degree || item.degree_type || item.qualification) {
        return item.degree || item.degree_type || item.qualification;
      }
      // For skills/certifications/languages
      return item.name || item.title || item.skill || item.certification_name || item.language || '';
    }
    return String(item || '');
  };

  // Helper to format education entries
  const formatEducation = (edu) => {
    if (typeof edu === 'string') return { degree: edu, institution: '', year: '' };
    return {
      degree: edu.degree || edu.degree_type || edu.qualification || '',
      institution: edu.institution || edu.institution_name || edu.university || edu.school || '',
      field: edu.field_of_study || edu.specialization || edu.major || '',
      year: edu.year || edu.graduation_year || edu.end_date || ''
    };
  };

  // Load candidate profile
  const loadProfile = useCallback(async () => {
    if (!profileIdentifier) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/public/profile/${profileIdentifier}`);
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Profile not found');
      }
      
      const data = await response.json();
      setCandidate(data.candidate);
      
      // Generate QR code
      const profileUrl = `${window.location.origin}/${data.candidate.username || profileIdentifier}`;
      const qr = await QRCode.toDataURL(profileUrl, {
        width: 150,
        margin: 2,
        color: { dark: '#1e293b', light: '#ffffff' }
      });
      setQrCodeUrl(qr);
      
      // Check if current user is the owner
      const token = localStorage.getItem('candidate_token');
      if (token) {
        try {
          const meRes = await fetch(`${API_URL}/api/candidate/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (meRes.ok) {
            const meData = await meRes.json();
            if (meData.id === data.candidate.id) {
              setIsOwner(true);
              loadMatches(data.candidate.id, token);
            }
          }
        } catch (e) {
          console.log('Not logged in as owner');
        }
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profileIdentifier]);

  // Load job matches (only for logged in owner)
  const loadMatches = async (candidateId, token) => {
    try {
      setLoadingMatches(true);
      const res = await fetch(`${API_URL}/api/candidate/matches?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMatches(data.matches || []);
      }
    } catch (e) {
      console.error('Error loading matches:', e);
    } finally {
      setLoadingMatches(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Handle claim profile - send OTP
  const handleSendClaimOtp = async () => {
    if (!claimPhone || claimPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setClaimLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/profile/${profileIdentifier}/claim/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: claimPhone })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }
      
      toast.success('OTP sent to your WhatsApp!');
      setClaimStep('otp');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClaimLoading(false);
    }
  };

  // Handle claim profile - verify OTP
  const handleVerifyClaimOtp = async () => {
    if (!claimOtp || claimOtp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    
    setClaimLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/profile/${profileIdentifier}/claim/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: claimPhone, otp: claimOtp })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Invalid OTP');
      }
      
      // Store token and redirect to portal
      localStorage.setItem('candidate_token', data.access_token);
      localStorage.setItem('candidate_user', JSON.stringify(data.user));
      
      toast.success('Profile claimed successfully!');
      setShowClaimModal(false);
      setIsOwner(true);
      
      // Navigate to candidate portal
      navigate('/my');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClaimLoading(false);
    }
  };

  // Share profile
  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${candidate?.name}'s Profile`,
          text: `Check out ${candidate?.name}'s professional profile`,
          url: url
        });
      } catch (e) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Profile link copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Profile Not Found</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/images/daruwallas-logo.png" 
              alt="Daruwallas" 
              className="h-8 object-contain"
            />
          </div>
          
          <div className="flex items-center gap-3">
            {!isOwner && (
              <Button
                onClick={() => setShowClaimModal(true)}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                size="sm"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Claim Profile
              </Button>
            )}
            {isOwner && (
              <Button
                onClick={() => navigate('/my')}
                variant="outline"
                size="sm"
              >
                <User className="w-4 h-4 mr-2" />
                My Portal
              </Button>
            )}
            <Button onClick={handleShare} variant="ghost" size="sm">
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Profile Header Card */}
        <Card className="mb-6 overflow-hidden border-0 shadow-xl">
          {/* Cover gradient */}
          <div className="h-32 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600" />
          
          <CardContent className="relative pt-0 pb-6 px-4 sm:px-6">
            {/* Avatar - Centered on mobile, left on desktop */}
            <div className="absolute -top-16 left-1/2 sm:left-6 -translate-x-1/2 sm:translate-x-0">
              <Avatar className="w-28 h-28 sm:w-32 sm:h-32 border-4 border-white shadow-xl">
                <AvatarImage src={candidate?.profile_picture?.startsWith('data:') || candidate?.profile_picture?.startsWith('http') ? candidate.profile_picture : (candidate?.profile_picture ? `${API_URL}${candidate.profile_picture}` : null)} />
                <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-800 text-white text-2xl sm:text-3xl font-bold">
                  {getInitials(candidate?.name)}
                </AvatarFallback>
              </Avatar>
              {candidate?.phone_verified && (
                <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              )}
            </div>

            {/* Profile Info */}
            <div className="ml-0 sm:ml-40 pt-16 sm:pt-4 px-2 sm:px-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{candidate?.name}</h1>
                    {/* Gender Icon */}
                    {candidate?.gender && candidate?.show_gender_public !== false && (
                      <span className={`text-lg ${candidate.gender === 'female' ? 'text-pink-500' : candidate.gender === 'male' ? 'text-blue-500' : 'text-slate-400'}`}>
                        {candidate.gender === 'female' ? '♀' : candidate.gender === 'male' ? '♂' : '⚥'}
                      </span>
                    )}
                  </div>
                  <p className="text-base sm:text-lg text-slate-600 mt-1">
                    {candidate?.current_designation || 'Professional'}
                    {candidate?.current_company && candidate?.show_company_public !== false && (
                      <span className="text-slate-400"> at {candidate.current_company}</span>
                    )}
                  </p>
                  
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-4 mt-3 text-sm text-slate-500">
                    {candidate?.current_location && candidate?.show_location_public !== false && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {candidate.current_location}
                      </span>
                    )}
                    {candidate?.total_years_experience !== undefined && candidate?.show_experience_years_public !== false && (
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {formatExperience(candidate.total_years_experience)}
                      </span>
                    )}
                    {candidate?.work_preference && candidate?.show_work_preference_public !== false && (
                      <Badge variant="outline" className="text-indigo-600 border-indigo-300 bg-indigo-50">
                        {candidate.work_preference === 'remote' ? '🏠 Remote' : 
                         candidate.work_preference === 'hybrid' ? '🔄 Hybrid' : 
                         candidate.work_preference === 'office' ? '🏢 Office' : '✨ Flexible'}
                      </Badge>
                    )}
                    {candidate?.phone_verified && (
                      <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                        <Shield className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Availability Badge */}
                <div className="text-center sm:text-right mt-4 sm:mt-0">
                  {candidate?.actively_looking !== false && (
                    <Badge className="bg-green-500 hover:bg-green-600 text-white">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Open to Work
                    </Badge>
                  )}
                  {candidate?.notice_period_days !== undefined && candidate?.show_notice_period_public !== false && (
                    <p className="text-sm text-slate-500 mt-2">
                      <Clock className="w-4 h-4 inline mr-1" />
                      {candidate.notice_period_days === 0 ? 'Immediate' : `${candidate.notice_period_days} days notice`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Professional Summary */}
            {candidate?.professional_summary && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    About
                  </h2>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                    {candidate.professional_summary}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Skills */}
            {(candidate?.technical_skills?.length > 0 || candidate?.soft_skills?.length > 0) && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Code className="w-5 h-5 text-blue-600" />
                    Skills & Expertise
                  </h2>
                  
                  {candidate?.technical_skills?.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Technical Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {candidate.technical_skills.map((skill, idx) => (
                          <Badge 
                            key={idx} 
                            className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1"
                          >
                            {displayItem(skill)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {candidate?.soft_skills?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Soft Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {candidate.soft_skills.map((skill, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline"
                            className="text-slate-600 px-3 py-1"
                          >
                            {displayItem(skill)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Education */}
            {candidate?.education?.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    Education
                  </h2>
                  <div className="space-y-4">
                    {candidate.education.map((edu, idx) => {
                      const formatted = formatEducation(edu);
                      return (
                        <div key={idx} className="flex gap-4">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <GraduationCap className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-800">
                              {formatted.degree || 'Education'}
                              {formatted.field && ` in ${formatted.field}`}
                            </h3>
                            {formatted.institution && (
                              <p className="text-sm text-slate-600">{formatted.institution}</p>
                            )}
                            {formatted.year && (
                              <p className="text-sm text-slate-400">{formatted.year}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Languages */}
            {candidate?.languages?.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Languages className="w-5 h-5 text-blue-600" />
                    Languages
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {candidate.languages.map((lang, idx) => (
                      <Badge 
                        key={idx} 
                        className="bg-purple-100 text-purple-700 px-3 py-1"
                      >
                        <Globe className="w-3 h-3 mr-1" />
                        {displayItem(lang)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {candidate?.certifications?.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5 text-blue-600" />
                    Certifications
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {candidate.certifications.map((cert, idx) => (
                      <Badge 
                        key={idx} 
                        className="bg-amber-100 text-amber-700 px-3 py-1"
                      >
                        <Award className="w-3 h-3 mr-1" />
                        {displayItem(cert)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Job Matches - Only shown to owner */}
            {isOwner && (
              <Card className="border-0 shadow-md border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-600" />
                    Your Job Matches
                    <Badge className="bg-green-100 text-green-700 ml-2">Private</Badge>
                  </h2>
                  
                  {loadingMatches ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : matches.length > 0 ? (
                    <div className="space-y-3">
                      {matches.map((match, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                          onClick={() => navigate(`/my/matches/${match.id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                              <Building2 className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-slate-800">{match.job_title}</h3>
                              <p className="text-sm text-slate-500">{match.company_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={`${
                              match.match_score >= 80 ? 'bg-green-100 text-green-700' :
                              match.match_score >= 60 ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {match.match_score}% Match
                            </Badge>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No job matches yet</p>
                  )}
                  
                  <Button 
                    onClick={() => navigate('/my/jobs')} 
                    variant="outline" 
                    className="w-full mt-4"
                  >
                    View All Matches
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Contact Information - Hidden for non-logged-in users */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-blue-600" />
                  Contact Information
                </h3>
                {isOwner ? (
                  // Owner can see their own contact info
                  <div className="space-y-3">
                    {candidate?.email && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                          <Globe className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Email</p>
                          <a href={`mailto:${candidate.email}`} className="text-slate-700 hover:text-blue-600">
                            {candidate.email}
                          </a>
                        </div>
                      </div>
                    )}
                    {candidate?.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center">
                          <Smartphone className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Phone</p>
                          <a href={`tel:${candidate.phone}`} className="text-slate-700 hover:text-green-600">
                            {candidate.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    {candidate?.linkedin_url && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center">
                          <ExternalLink className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">LinkedIn</p>
                          <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-slate-700 hover:text-indigo-600">
                            View Profile
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Non-logged-in users see login prompt
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Lock className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm text-slate-600 mb-3">
                      Login to view contact information
                    </p>
                    <p className="text-xs text-slate-400 mb-4">
                      Only registered HR & Recruiters can view candidate contact details
                    </p>
                    <Button
                      onClick={() => window.location.href = '/ats/login'}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Login as HR / Recruiter
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* QR Code Card */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6 text-center">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Share Profile</h3>
                {qrCodeUrl && (
                  <div className="bg-white p-3 rounded-lg inline-block shadow-inner border">
                    <img src={qrCodeUrl} alt="Profile QR Code" className="w-32 h-32" />
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-3">
                  Scan to view this profile
                </p>
                <Button 
                  onClick={handleShare}
                  variant="outline" 
                  size="sm"
                  className="mt-3 w-full"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">Profile Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 text-sm">Experience</span>
                    <span className="font-semibold text-slate-800">
                      {formatExperience(candidate?.total_years_experience)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 text-sm">Skills</span>
                    <span className="font-semibold text-slate-800">
                      {(candidate?.technical_skills?.length || 0) + (candidate?.soft_skills?.length || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 text-sm">Languages</span>
                    <span className="font-semibold text-slate-800">
                      {candidate?.languages?.length || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 text-sm">Certifications</span>
                    <span className="font-semibold text-slate-800">
                      {candidate?.certifications?.length || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Claim Profile CTA - for non-owners */}
            {!isOwner && (
              <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-6 text-center">
                  <Lock className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-2">Is this your profile?</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Claim ownership to edit your details and see job matches
                  </p>
                  <Button
                    onClick={() => setShowClaimModal(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Smartphone className="w-4 h-4 mr-2" />
                    Claim via WhatsApp OTP
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Claim Profile Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-2">Claim Your Profile</h2>
              <p className="text-slate-600 mb-6">
                Verify your phone number via WhatsApp OTP to claim this profile
              </p>

              {claimStep === 'phone' ? (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={claimPhone}
                      onChange={(e) => setClaimPhone(e.target.value)}
                      placeholder="Enter your phone number"
                      className="text-lg"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Must match the phone number on this profile
                    </p>
                  </div>
                  <Button
                    onClick={handleSendClaimOtp}
                    disabled={claimLoading}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {claimLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Smartphone className="w-4 h-4 mr-2" />
                    )}
                    Send OTP via WhatsApp
                  </Button>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Enter OTP
                    </label>
                    <Input
                      type="text"
                      value={claimOtp}
                      onChange={(e) => setClaimOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-digit OTP"
                      className="text-2xl text-center tracking-widest"
                      maxLength={6}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Check your WhatsApp for the OTP
                    </p>
                  </div>
                  <Button
                    onClick={handleVerifyClaimOtp}
                    disabled={claimLoading || claimOtp.length !== 6}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {claimLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Verify & Claim
                  </Button>
                  <Button
                    onClick={() => setClaimStep('phone')}
                    variant="ghost"
                    className="w-full mt-2"
                  >
                    Change Phone Number
                  </Button>
                </>
              )}

              <Button
                onClick={() => {
                  setShowClaimModal(false);
                  setClaimStep('phone');
                  setClaimOtp('');
                }}
                variant="ghost"
                className="w-full mt-4"
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
